import { useEffect, useState } from 'react'
import HighlightIcon from '@mui/icons-material/Highlight'
import { Stack, Typography } from '@mui/material'
import { useControlStore } from '@editor/stores'
import { CorneredSection } from '@ui2/structure'
import { ActiveIconButton, DeleteIconButton } from '@ui/actions'
import {
  IfcElementTypeAssignment,
  IfcStoreyAssignment,
} from '../Controls/IFCUploadWidget/types/ifcMetadata'

interface IFCMetadataAssignmentsSummaryProps {
  filteredElements: string[][]
  elementTypeAssignments: IfcElementTypeAssignment[][]
  storeyAssignments: IfcStoreyAssignment[][]
  onRemoveFilteredEntry: (index: number) => void
}

const IFCMetadataAssignmentsSummary = ({
  filteredElements,
  elementTypeAssignments,
  storeyAssignments,
  onRemoveFilteredEntry,
}: IFCMetadataAssignmentsSummaryProps) => {
  const setHighlightedIfcElementGuids = useControlStore(
    state => state.setHighlightedIfcElementGuids,
  )
  const [activeIndicesFiltered, setActiveIndicesFiltered] = useState<number[]>([])
  const [hoveredGuids, setHoveredGuids] = useState<string[]>([])

  useEffect(() => {
    const highlightedGuids = [
      ...activeIndicesFiltered.flatMap(index => filteredElements[index]),

      ...hoveredGuids,
    ]

    setHighlightedIfcElementGuids(highlightedGuids)

    return () => setHighlightedIfcElementGuids([])
  }, [
    activeIndicesFiltered,
    filteredElements,
    elementTypeAssignments,
    hoveredGuids,
    setHighlightedIfcElementGuids,
    storeyAssignments,
  ])

  const filteredElementsContent = filteredElements.length > 0 && (
    <Stack spacing={0.75}>
      {filteredElements.map((item, index) => (
        <Stack
          key={`${item.join('-')}-${index}`}
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2">
            {item.length} {item.length === 1 ? 'Element' : 'Elemente'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <ActiveIconButton
              isActive={activeIndicesFiltered.includes(index)}
              sx={{ padding: 0 }}
              onPointerEnter={() => setHoveredGuids(item)}
              onPointerLeave={() => setHoveredGuids([])}
              onClick={() =>
                setActiveIndicesFiltered(
                  activeIndicesFiltered.includes(index)
                    ? activeIndicesFiltered.filter(i => i !== index)
                    : [...activeIndicesFiltered, index],
                )
              }
            >
              <HighlightIcon fontSize="small" />
            </ActiveIconButton>
            <DeleteIconButton
              tooltip="Entfernen"
              onClick={() => {
                onRemoveFilteredEntry(index)
                setActiveIndicesFiltered(
                  activeIndicesFiltered.filter(i => i !== index).map(i => (i > index ? i - 1 : i)),
                )
              }}
              sx={{ padding: 0, '& svg': { fontSize: 20 } }}
            />
          </Stack>
        </Stack>
      ))}
    </Stack>
  )

  return (
    <CorneredSection title="Gefilterte Elemente" collapsible defaultOpen={false}>
      {filteredElementsContent}
    </CorneredSection>
  )
}

export default IFCMetadataAssignmentsSummary
